/**
 * Colonia Matching Utilities
 *
 * Fuzzy-match colonia (neighborhood) names against a SEPOMEX list.
 * Used only by AddressFormSepomex (the buró authorization page) to detect
 * whether a user's previously-typed colonia is still valid after the CP
 * changes.
 */
import { distance } from "fastest-levenshtein";

import { normalizeText } from "./normalizeAddress";

/**
 * Find best matching colonia using Levenshtein distance
 *
 * @param query - The colonia name to match
 * @param options - Array of available colonia names (e.g., from SEPOMEX)
 * @param threshold - Minimum similarity score (0-1) to consider a match. Default: 0.6
 * @returns The best matching colonia name, or null if no match above threshold
 *
 * @example
 * findBestColoniaMatch("Roma Nte.", ["Roma Norte", "Roma Sur"]) // => "Roma Norte"
 * findBestColoniaMatch("Del Valle", ["Del Valle Centro", "Del Valle Sur"]) // => "Del Valle Centro"
 */
export const findBestColoniaMatch = (
  query: string,
  options: string[],
  threshold: number = 0.6,
): string | null => {
  if (!query || options.length === 0) return null;

  const normalizedQuery = normalizeText(query);

  const matches = options
    .map((opt) => {
      const normalizedOpt = normalizeText(opt);
      const maxLen = Math.max(normalizedQuery.length, normalizedOpt.length);

      if (maxLen === 0) return { opt, similarity: 0 };

      // Calculate Levenshtein distance and convert to similarity score
      const dist = distance(normalizedQuery, normalizedOpt);
      const similarity = 1 - dist / maxLen;

      return { opt, similarity };
    })
    .filter((m) => m.similarity >= threshold)
    .sort((a, b) => b.similarity - a.similarity);

  return matches[0]?.opt ?? null;
};
